/* angular/components/sharpr/google/googleOauth.js and a-react-app/src/libs/google/googleOauth.js should be identical and kept in sync.
	Run `node a-react-app/scripts/copy-other.js` to copy the angular version to the react directory. */

import dayjs from 'dayjs';

export class GoogleOauth {
	async getUserToken(userId, options = {}) {
		return await fetch(`${window.BASE_URL}/api/v4/oauth_credentials?type=google&user_id=${userId}`, options);
	}

	/**
	 * Constructs the Google OAuth URL
	 * @see param descriptions here: https://developers.google.com/identity/protocols/oauth2/javascript-implicit-flow?hl=en#redirecting
	 * @param {String} userEmail
	 * @param {String} scope  The access scope to request from the user. See https://developers.google.com/drive/api/v3/about-authscope
	 * @returns {Promise<String>}
	 */
	constructGoogleOAuthUrl(userEmail, scope, authHeader) {
		this.randomState = Math.random().toString(36).slice(2);

		return fetch(`${window.BASE_URL}/api/v2/config/preferences`, {
			method: 'GET',
			headers: { ...authHeader },
		})
			.then(resp => resp.json())
			.then(preferences => {
				console.debug('preferences response data', preferences);

				let oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth';
				const params = {
					// available on the google dev console
					client_id: preferences['Integrations.google_oauth_client_id'],
					// also defined in the google dev console on the Credentials page
					redirect_uri: `${window.BASE_URL}/oauth/google`,
					response_type: 'token',
					scope,
					state: this.randomState, // create random string. See how the angular box integration does it
					// google will prefill the login screen with this email
					login_hint: userEmail,
				};

				const url = oauth2Endpoint + '?' + new URLSearchParams(params).toString();

				return url;
			});
	}

	/**
	 * Open a popup window with a URL to the Google Oauth page
	 * @param {String} userEmail
	 * @param {String} scope  The access scope to request from the user. See https://developers.google.com/drive/api/v3/about-auth
	 * @returns {Window}
	 */
	async beginOauth(userEmail, scope, authHeader) {
		const url = await this.constructGoogleOAuthUrl(userEmail, scope, authHeader);
		const constrain = num => Math.max(0, Math.round(num));
		const width = 490;
		const height = 775;
		const top = constrain(window.outerHeight / 2 + window.screenY - height / 2);
		const left = constrain(window.outerWidth / 2 + window.screenX - width / 2);

		try {
			// if the user has already authorized sharpr, google skips the authorize dialog and sends them back to the redirect_url
			const oauthWindow = window.open(url, 'Google OAuth', `top=${top},left=${left},width=${width},height=${height}`);
			return oauthWindow;
		} catch (e) {
			// one of these errors: https://developers.google.com/identity/protocols/oauth2/javascript-implicit-flow?hl=en#authorization-errors
			console.log('e', e);
		}
	}

	/**
	 * Save the oauth token and info to Sharpr
	 * @param {String} access_token  A token to use in requests to Google APIs
	 * @param {Int} expires_in  The number of seconds until `access_token` expires
	 * @param {Object} authHeader  The auth header to send with requests to Sharpr `/api/v4` endpoints
	 * @returns {Promise<boolean>}
	 */
	async saveOauthToken(access_token, expires_in, authHeader = {}) {
		// subtract 300 seconds (5 minutes) for safety since we don't know how much time has passed since google calculated expires_in
		/// multiply by 1000 b/c the usages below expect milliseconds
		const expiresInMilliseconds = (expires_in - 300) * 1000;
		// Date.now() provides timestamp in milliseconds, and dayjs accepts the timestamp in milliseconds.
		const currentTimestamp = Date.now();
		const expiresTimestamp = currentTimestamp + expiresInMilliseconds;
		const body = JSON.stringify({
			token: access_token,
			type: 'google',
			expires: dayjs(expiresTimestamp).toISOString(),
		});
		console.debug('sending this data to POST /v4/oauth_credentials:', JSON.parse(body));
		const resp = await fetch(`${window.BASE_URL}/api/v4/oauth_credentials`, {
			method: 'POST',
			body,
			headers: { 'Content-Type': 'application/json', ...authHeader },
		});
		console.debug('response from /v4/oauth_credentials', resp);

		if (resp.status !== 201) {
			alert('Error saving OAuth credentials. Please refresh the page and retry the upload.');
			return false;
		}

		return true;
	}

	/**
	 * Determine if the oauth process was successful. Return error details if not.
	 * @param {Object} resp  The response from `app/views/templates/oauth/google.ctp`.
	 * @returns {success: boolean, message: string}
	 */
	checkOauthSuccess(resp) {
		console.debug('handleOauthResponse. params sent from google:', resp);
		if (resp.error) {
			console.error('google sent an error code', resp.error);
			return {
				success: false,
				message: `Authorization failed with the error code: ${resp.error}. Skipping file upload.`,
			};
		}
		console.assert(this.randomState === resp.state, 'our generated state is the same as what we got from google');
		if (this.randomState !== resp.state) {
			// can't prove this is coming from a request sharpr initialized
			return {
				success: false,
				message: `Authorization failed. The response could not be authenticated. Skipping file upload.`,
			};
		}

		return { success: true, message: '' };
	}

	/**
	 * Runs steps for complete Oauth process:
	 * 1. Open popup to Google auth screen
	 * 2. Listen for message back from app/views/templates/oauth/google.ctp after authorization completed by user
	 * 3. Save token if successful or reject and return error message
	 * @param {String} userEmail
	 * @param {String} scope  The access scope to request from the user. See https://developers.google.com/drive/api/v3/about-auth
	 * for Google Drive scopes. Other Google services will have other scope options.
	 * @param {Object} authHeader  The auth header to send with requests to Sharpr `/api/v4` endpoints
	 * @returns {Promise<Object>}
	 */
	async getAndSaveToken(userEmail, scope, authHeader = {}) {
		console.debug('did not find a valid record. sending through oauth process...');
		await this.beginOauth(userEmail, scope, authHeader);

		// wait for a message from google.ctp. It will send over the results when the user is done with oauth.
		return new Promise((resolveMessenger, rejectMessenger) => {
			const self = this;
			async function handleMessageFromGoogle(event) {
				window.removeEventListener('message', handleMessageFromGoogle);
				if (event.origin !== window.BASE_URL) {
					rejectMessenger({
						success: false,
						message: 'The page received a message from and unknown source. Ignoring.',
					});
				}
				if (event.data && event.data.action && event.data.action === 'google_oauth_complete') {
					const oauthSuccess = self.checkOauthSuccess(event.data.params);
					console.debug('oauthSuccess?', oauthSuccess);
					if (oauthSuccess.success === true) {
						await self.saveOauthToken(event.data.params.access_token, event.data.params.expires_in, authHeader);
						resolveMessenger({ success: true, token: event.data.params.access_token });
					} else {
						rejectMessenger({ success: false, message: oauthSuccess.message });
					}
				}
			}
			window.addEventListener('message', handleMessageFromGoogle);
		});
	}
}

export const googleOauth = new GoogleOauth();
